import { plainHeadingText } from '../fmHostedHeading';
import { CHART_CHROME_KEYS, validateChart, validateChartChrome } from './chartFields.validation';
import {
  assertArray,
  assertNonEmptyString,
  assertRecord,
  rejectUnknownKeys,
  validateAction,
  validateEnum,
  validateOptionalBoolean,
  validateOptionalCallback,
  validateOptionalFiniteNumber,
  validateOptionalString,
  validateStringArray,
} from './elementFields.validation';
import {
  validateLiveMapSlotProps,
  validateMapCallouts,
  validateMapLegend,
  validateMapSteps,
} from './mapFields.validation';

function validateContextualMedia(value: unknown, label: string): void {
  assertRecord(value, label);
  rejectUnknownKeys(
    value,
    [
      'scope',
      'species',
      'developmentClass',
      'landClass',
      'structure',
      'condition',
      'reportedCause',
      'basisLabel',
      'sourceRevision',
      'freshness',
      'actualMedia',
      'allowIllustrativeFallback',
      'stateId',
    ],
    label,
  );
  validateEnum(value.scope, ['editorial', 'property', 'stand'], `${label}.scope`);
  validateEnum(
    value.species,
    ['pine', 'spruce', 'birch', 'deciduous', 'mixed', 'other', 'unknown'],
    `${label}.species`,
    true,
  );
  validateEnum(
    value.developmentClass,
    ['A0', 'T1', 'T2', 'unknown'],
    `${label}.developmentClass`,
    true,
  );
  validateEnum(
    value.landClass,
    ['productive', 'low-productive', 'nonproductive-peatland', 'other', 'unknown'],
    `${label}.landClass`,
    true,
  );
  validateEnum(
    value.structure,
    [
      'pine-overstory-spruce-regeneration',
      'birch-overstory-spruce-regeneration',
      'other',
      'unknown',
    ],
    `${label}.structure`,
    true,
  );
  validateEnum(
    value.condition,
    ['conifer-mortality', 'broken-trees', 'other', 'unknown'],
    `${label}.condition`,
    true,
  );
  validateEnum(
    value.reportedCause,
    [
      'insect-or-parasite',
      'pathogen',
      'wind',
      'snow',
      'fire',
      'drought',
      'machinery',
      'other',
      'unknown',
    ],
    `${label}.reportedCause`,
    true,
  );
  ['basisLabel', 'sourceRevision', 'freshness', 'stateId'].forEach((key) =>
    validateOptionalString(value, key, label),
  );
  if (typeof value.allowIllustrativeFallback !== 'boolean') {
    throw new Error(`Invalid descriptor: ${label}.allowIllustrativeFallback must be a boolean.`);
  }
  if (value.actualMedia !== undefined) {
    const actualLabel = `${label}.actualMedia`;
    assertRecord(value.actualMedia, actualLabel);
    rejectUnknownKeys(value.actualMedia, ['identity', 'evidenceKind'], actualLabel);
    assertNonEmptyString(value.actualMedia.identity, `${actualLabel}.identity`);
    validateEnum(
      value.actualMedia.evidenceKind,
      ['field-image', 'inspection-map', 'orthophoto', 'canopy-raster', 'property-image', 'other'],
      `${actualLabel}.evidenceKind`,
    );
  }
}

function validateMediaSource(value: unknown, label: string): void {
  assertRecord(value, label);
  rejectUnknownKeys(value, ['label', 'timestamp', 'licenseLabel'], label);
  assertNonEmptyString(value.label, `${label}.label`);
  ['timestamp', 'licenseLabel'].forEach((key) => validateOptionalString(value, key, label));
}

/**
 * The figure caption, the source line and the interaction hint render as
 * separate lines under one media figure, so the same string in two of them
 * prints the same sentence twice.
 */
function rejectRepeatedMediaCopy(slots: Record<string, unknown>, label: string): void {
  const seen = new Map<string, string>();
  Object.entries(slots).forEach(([slot, text]) => {
    const key = plainHeadingText(typeof text === 'string' ? text : null);
    if (key === null) return;
    const first = seen.get(key);
    if (first !== undefined)
      throw new Error(
        `Invalid descriptor: ${label}.${slot} repeats ${label}.${first} ("${String(text)}"); each caption, source and hint line must say something different.`,
      );
    seen.set(key, slot);
  });
}

function sourceLabel(source: unknown): unknown {
  if (typeof source !== 'object' || source === null || !('label' in source)) return undefined;
  return source.label;
}

function figureCaption(figure: unknown): unknown {
  if (typeof figure !== 'object' || figure === null || !('slotProps' in figure)) return undefined;
  const { slotProps } = figure;
  if (typeof slotProps !== 'object' || slotProps === null || !('caption' in slotProps))
    return undefined;
  return slotProps.caption;
}

export const IMAGE_MEDIA_KEYS = [
  'assetKey',
  'mediaContext',
  'altOverride',
  'caption',
  'credit',
  'source',
  'emptyLabel',
] as const;

export const MAP_MEDIA_KEYS = [
  'figure',
  'layoutPreset',
  'intro',
  'steps',
  'selectedStepId',
  'onStepChange',
  'stepControl',
  'stepLabel',
  'callouts',
  'selectedCalloutId',
  'onSelectCallout',
  'legend',
  'source',
  'interactionHint',
  'loading',
  'errorLabel',
  'emptyLabel',
  'reveal',
] as const;

// eslint-disable-next-line complexity -- every registered media discriminator is rejected or validated here.
export function validateMedia(value: unknown, label: string): void {
  assertRecord(value, label);
  assertNonEmptyString(value.kind, `${label}.kind`);
  if (value.kind === 'image') {
    rejectUnknownKeys(value, ['kind', ...IMAGE_MEDIA_KEYS, 'testId'], label);
    const hasAsset = Object.prototype.hasOwnProperty.call(value, 'assetKey');
    const hasContext = Object.prototype.hasOwnProperty.call(value, 'mediaContext');
    if (hasAsset === hasContext)
      throw new Error(`Invalid descriptor: ${label} requires exactly one governed image source.`);
    if (hasAsset) assertNonEmptyString(value.assetKey, `${label}.assetKey`);
    if (hasContext) validateContextualMedia(value.mediaContext, `${label}.mediaContext`);
    ['altOverride', 'caption', 'credit', 'emptyLabel', 'testId'].forEach((key) =>
      validateOptionalString(value, key, label),
    );
    if (value.source !== undefined) validateMediaSource(value.source, `${label}.source`);
    rejectRepeatedMediaCopy(
      { caption: value.caption, 'source.label': sourceLabel(value.source) },
      label,
    );
    return;
  }
  if (value.kind === 'chart') {
    rejectUnknownKeys(value, ['kind', 'chart', ...CHART_CHROME_KEYS], label);
    validateChartChrome(value, label);
    validateChart(value.chart, `${label}.chart`);
    return;
  }
  if (value.kind === 'map' || value.kind === 'active-content') {
    rejectUnknownKeys(value, ['kind', ...MAP_MEDIA_KEYS, 'testId'], label);
    if (value.figure !== undefined) {
      assertRecord(value.figure, `${label}.figure`);
      if (value.figure.kind === 'asset') {
        rejectUnknownKeys(value.figure, ['kind', 'assetKey', 'altOverride'], `${label}.figure`);
        assertNonEmptyString(value.figure.assetKey, `${label}.figure.assetKey`);
        validateOptionalString(value.figure, 'altOverride', `${label}.figure`);
      } else if (value.figure.kind === 'live') {
        rejectUnknownKeys(value.figure, ['kind', 'slotKey', 'slotProps'], `${label}.figure`);
        assertNonEmptyString(value.figure.slotKey, `${label}.figure.slotKey`);
        validateLiveMapSlotProps(
          value.figure.slotKey,
          value.figure.slotProps,
          `${label}.figure.slotProps`,
        );
      } else throw new Error(`Invalid descriptor: ${label}.figure.kind is not registered.`);
    }
    validateEnum(
      value.layoutPreset,
      [
        'media-right',
        'media-left',
        'stacked',
        'full-width-with-callout-rail',
        'report-snapshot',
        'timeline-focus',
      ],
      `${label}.layoutPreset`,
      true,
    );
    [
      'intro',
      'selectedStepId',
      'stepLabel',
      'selectedCalloutId',
      'interactionHint',
      'errorLabel',
      'emptyLabel',
      'testId',
    ].forEach((key) => validateOptionalString(value, key, label));
    validateEnum(value.stepControl, ['segmented', 'radio'], `${label}.stepControl`, true);
    ['loading', 'reveal'].forEach((key) => validateOptionalBoolean(value, key, label));
    ['onStepChange', 'onSelectCallout'].forEach((key) =>
      validateOptionalCallback(value, key, label),
    );
    if (value.steps !== undefined) validateMapSteps(value.steps, `${label}.steps`);
    if (value.callouts !== undefined) validateMapCallouts(value.callouts, `${label}.callouts`);
    if (value.legend !== undefined) validateMapLegend(value.legend, `${label}.legend`);
    if (value.source !== undefined) validateMediaSource(value.source, `${label}.source`);
    rejectRepeatedMediaCopy(
      {
        'figure.slotProps.caption': figureCaption(value.figure),
        'source.label': sourceLabel(value.source),
        interactionHint: value.interactionHint,
      },
      label,
    );
    return;
  }
  throw new Error(`Invalid descriptor: unregistered media kind "${String(value.kind)}".`);
}

/**
 * The card prints its title right above its button, so a button repeating the
 * title says the same words twice; the button names the verb, the title the
 * destination. A tag repeating the button is the same echo one line higher.
 */
function rejectCtaEchoingTitle(item: Record<string, unknown>, label: string): void {
  const ctaLabel = plainHeadingText(
    typeof item.cta === 'object' && item.cta !== null && 'label' in item.cta
      ? String(item.cta.label)
      : null,
  );
  if (ctaLabel === null) return;
  if (plainHeadingText(String(item.label)) === ctaLabel) {
    throw new Error(
      `Invalid descriptor: ${label}.cta.label repeats ${label}.label ("${String(item.label)}"); the button names the action, the title names the destination.`,
    );
  }
  const tags = Array.isArray(item.tags) ? item.tags : [];
  const echoedTag = tags.find((tag) => plainHeadingText(String(tag)) === ctaLabel);
  if (echoedTag !== undefined) {
    throw new Error(
      `Invalid descriptor: ${label}.tags repeats ${label}.cta.label ("${String(echoedTag)}").`,
    );
  }
}

export function validateActionSet(value: unknown, label: string): void {
  assertRecord(value, label);
  const actionSet = value;
  rejectUnknownKeys(
    actionSet,
    [
      'presentation',
      'items',
      'columns',
      'autoCycleMs',
      'mediaSide',
      'activeKey',
      'defaultActiveKey',
      'onChange',
      'testId',
    ],
    label,
  );
  assertArray(actionSet.items, `${label}.items`);
  actionSet.items.forEach((item, index) => {
    const itemLabel = `${label}.items[${index}]`;
    assertRecord(item, itemLabel);
    rejectUnknownKeys(
      item,
      [
        'key',
        'label',
        'description',
        'tags',
        'iconKey',
        'media',
        'status',
        'statusLabel',
        'disabledReason',
        'cta',
        'testId',
      ],
      itemLabel,
    );
    ['key', 'label', 'description'].forEach((key) =>
      assertNonEmptyString(item[key], `${itemLabel}.${key}`),
    );
    if (item.tags !== undefined) validateStringArray(item.tags, `${itemLabel}.tags`);
    validateEnum(item.iconKey, ['currency', 'clock', 'calendar'], `${itemLabel}.iconKey`, true);
    validateEnum(
      item.status,
      ['available', 'active', 'denied', 'pending', 'unavailable'],
      `${itemLabel}.status`,
      true,
    );
    ['statusLabel', 'disabledReason', 'testId'].forEach((key) =>
      validateOptionalString(item, key, itemLabel),
    );
    validateAction(item.cta, `${itemLabel}.cta`);
    rejectCtaEchoingTitle(item, itemLabel);
    if (item.media !== undefined) validateMedia(item.media, `${itemLabel}.media`);
  });
  validateEnum(actionSet.presentation, ['switcher', 'cards'], `${label}.presentation`, true);
  validateEnum(actionSet.mediaSide, ['left', 'right'], `${label}.mediaSide`, true);
  if (actionSet.columns !== undefined && actionSet.columns !== 2 && actionSet.columns !== 3) {
    throw new Error(`Invalid descriptor: ${label}.columns must be 2 or 3.`);
  }
  validateOptionalFiniteNumber(actionSet, 'autoCycleMs', label);
  ['activeKey', 'defaultActiveKey', 'testId'].forEach((key) =>
    validateOptionalString(actionSet, key, label),
  );
  validateOptionalCallback(actionSet, 'onChange', label);
}
